import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {camelCaseToTitleCase} from '@aviary/store/transformation.helpers';
import {MatError, MatFormField, MatFormFieldControl, MatLabel} from '@angular/material/form-field';
import {pairwise, startWith} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {isDefined, isPopulatedString, isUndefined} from '@aviary/store/common/typing.helpers';
import {updateEnablementFor} from '@aviary/forms/form.helpers';
import {NgClass} from '@angular/common';
import {FieldErrorComponent} from '@aviary/forms/field-error.component';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {MatDatepickerInput} from '@angular/material/datepicker';

@Component({
  selector: 'mx-base-form-field',
  templateUrl: './base-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatLabel, MatFormField, NgClass, MatError, FieldErrorComponent]
})
export class BaseFormFieldComponent implements OnInit, OnChanges {

  @ViewChild(MatFormField, {static: true}) public matFormField: MatFormField;
  @ContentChild(MatFormFieldControl, {static: true}) public formFieldControlToConnect: MatFormFieldControl<unknown>;

  @Input({required: true}) public fieldName: string;

  @Input()
  public set overrideLabel(overrideLabel: string | undefined) {
    if (isDefined(overrideLabel)) {
      this.label = overrideLabel;
    } else if (isDefined(this.fieldName)) {
      // If the field name hasn't been set yet (like if this input was set before that one at initialization time), we'll take care of it in ngOnInit().
      this.label = camelCaseToTitleCase(this.fieldName);
    }
  }

  @Input() public externalLabel: boolean = false;
  @Input() public uppercaseExternalLabel = true;
  @Input() public hintLabel: string = '';
  @Input({required: true}) public formGroup: ExtendedFormGroup;
  @Input({required: true}) public control: ExtendedFormControl;
  @Input() public focused: boolean = false;
  @Input() public enabled: boolean = true;
  @Input() public showValidationErrors: boolean = true;

  public label: string;

  public get styleExternalLabelAsError(): boolean {
    return this.showValidationErrors && this.control.touched && !this.control.valid;
  }

  public get styleExternalLabelAsFocused(): boolean {
    return !this.styleExternalLabelAsError && this.focused;
  }

  public ngOnChanges({control, enabled}: SimpleChanges): void {
    /*
      Whenever the latest changes includes "control" or "enabled", we want to enable/disable the control.

      Note that we use the values of the actual input properties when setting, not the "currentValue" from the SimpleChanges object, since we don't know
      which is present in any given invocation of this function.  This way we don't have to worry about the order these inputs happen to get defined.
    */
    if (control || enabled) updateEnablementFor(this.control, this.enabled);
  }

  public ngOnInit(): void {
    this.initializeLabel();
    this.connectProjectedFormControlToFormFieldContainer();
    this.ensureAsyncValidationErrorsAppear();
  }

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private initializeLabel(): void {
    // If label wasn't already set by overrideLabel, then we default to the field name (at least until the override label is set later).
    if (isUndefined(this.label)) this.label = camelCaseToTitleCase(this.fieldName);
  }

  private connectProjectedFormControlToFormFieldContainer(): void {
    // Avoid double-registering Material datepicker inputs.
    if ((this.formFieldControlToConnect as unknown) instanceof MatDatepickerInput) {
      return;  // Let Angular Material handle its own input registration.
    }

    /*
      This is a bit of a hack, since we're updating a private property of MatFormField (which could cause a bug with a future version of Angular Material if
      they no longer make this property available. However, this is the secret to being able to create a MatFormField that works with projected content, which
      is the purpose of this reusable base component.

      This prevents an exception whose message is "mat-form-field must contain a MatFormFieldControl".  Because the control is not a direct child of the form
      field, Angular Material's code can't find it.  So this explicitly makes the connection on the form field's behalf, and voila - no more exception.

      The idea comes from https://ngserve.io/angular-material-tutorial-create-a-reusable-form-field-component/.
    */
    this.matFormField._control = this.formFieldControlToConnect;
  }

  private ensureAsyncValidationErrorsAppear(): void {
    /*
      Without this workaround, async validation errors (such as those that require an API round trip to calculate) don't appear until further user interaction
      occurs.

      We watch the form control for status changes, and anytime we get one that isn't PENDING (which is the state while we're waiting for the async
      validation to complete), we mark this component to be checked by the change detector.  This serves our needs, because after the async validation is
      complete, the status will change from PENDING to VALID or INVALID, either of which is a good reason to check for changes so that the form field's
      error state gets updated visually.

      Something like this could be expensive in terms of DOM changes if we're not careful, but we minimize the expense by ignoring consecutive change
      events that report the same status.  (We do listen for these events regardless of whether this form field's control uses an async validator because our
      extended form entities all delay adding their async validators.  So if we filtered on async validator presence, we might miss some that were added after
      construction was complete.  The tiny expense of watching all status changes and rarely needing to act on them is worth avoiding difficult timing bugs.)

      The idea comes from https://github.com/angular/angular/issues/44295#issuecomment-1550095352, and the issue containing the comment describes the issue
      (along with a bunch of amusing replies from the OP when everyone tried to supply workarounds instead of acknowledging the bug!).
    */

    this.control.statusChanges
      .pipe(
        startWith(this.control.status),   // Make sure we always have a first pair to emit.
        pairwise(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([previousStatus, currentStatus]) => {
        if (previousStatus === currentStatus) return;

        // If the async validator resolved and the value is invalid, mark as touched to make the error visible in case the user hasn't blurred the control yet.
        if (isPopulatedString(this.control.value) && previousStatus === 'PENDING' && currentStatus === 'INVALID' && !this.control.touched) {
          this.control.markAsTouched({onlySelf: true});
        }

        if (currentStatus !== 'PENDING') {
          this.changeDetector.markForCheck();
        }
      });
  }
}
